import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import type { MatCheckboxChange } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { UploadUiDialogComponent } from '../upload-ui-dialog/upload-ui-dialog.component';
import { BytetounitPipe } from '../../bytetounit.pipe';
import { ErrorService } from '../error.service';
import { ImagingStudyLocalImage } from './imaging-study-images.util';

@Component({
    selector: 'app-imaging-study-images',
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatButtonModule,
        MatCardModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
        MatDialogModule,
        ConfirmDialogComponent,
        UploadUiDialogComponent,
        BytetounitPipe
    ],
    templateUrl: './imaging-study-images.component.html',
    styleUrl: './imaging-study-images.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImagingStudyImagesComponent {
    private errorService = inject(ErrorService);

    @Input() uploadLabel = 'Upload images';
    @Input() allowedFiles: RegExp = /\.(jpg|jpeg|png|gif|webp|bmp)$/i;
    @Input() allowedFilesCategoryLabel = 'Images (jpg, png, gif, webp, bmp)';
    @Input() maxFileSize = 30000; // 10MB
    @Input() maxFiles = 10;
    @Input() allowMultipleSelection = true;
    /** Max size (bytes) allowed for inline/base64 embedding. Files larger than this will prompt for cloud upload. */
    @Input() inlineMaxFileSize = 30000; // 30kb default

    @Input() set existingImages(value: ImagingStudyLocalImage[] | null | undefined) {
        this.images = Array.isArray(value) ? [...value] : [];
        this.emitChanges();
    }

    @Output() imagesChanged = new EventEmitter<ImagingStudyLocalImage[]>();
    @Output() selectedImagesChanged = new EventEmitter<ImagingStudyLocalImage[]>();
    /** Emitted when files are uploaded via cloud UploadUiComponent. Payload is the same metadata UploadUi emits. */
    @Output() cloudFiles = new EventEmitter<any[]>();

    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    images: ImagingStudyLocalImage[] = [];

    isReading = false;
    private currentReader: FileReader | null = null;
    private pendingReplaceId: string | null = null;
    private dialog = inject(MatDialog);

    openPickerForAdd(event?: Event): void {
        event?.stopPropagation();
        this.pendingReplaceId = null;
        this.openNativePicker(true);
    }

    openPickerForReplace(imageId: string, event?: Event): void {
        event?.stopPropagation();
        this.pendingReplaceId = imageId;
        this.openNativePicker(false);
    }

    private openNativePicker(allowMultiple: boolean): void {
        if (!this.fileInput?.nativeElement) return;
        this.fileInput.nativeElement.value = '';
        this.fileInput.nativeElement.multiple = allowMultiple;
        this.fileInput.nativeElement.click();
    }

    onFileInputChanged(event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = input.files;
        if (!files || files.length === 0) return;

        if (this.pendingReplaceId) {
            const id = this.pendingReplaceId;
            this.pendingReplaceId = null;
            void this.replaceWithFile(id, files[0]);
            return;
        }

        void this.addFiles(files);
    }

    cancelRead(event?: Event): void {
        event?.stopPropagation();
        try {
            this.currentReader?.abort();
        } finally {
            this.isReading = false;
            this.currentReader = null;
        }
    }

    removeImage(imageId: string, event?: Event): void {
        event?.stopPropagation();
        this.images = (this.images || []).filter((i) => i.id !== imageId);
        this.emitChanges();
    }

    toggleSelected(imageId: string, checked: boolean, event?: Event | MatCheckboxChange): void {
        // MatCheckbox emits MatCheckboxChange (not a DOM Event)
        const maybeStop = (event as any)?.stopPropagation;
        if (typeof maybeStop === 'function') maybeStop.call(event);

        if (!this.allowMultipleSelection && checked) {
            this.images = (this.images || []).map((i) => ({ ...i, selected: i.id === imageId }));
        } else {
            this.images = (this.images || []).map((i) => (i.id === imageId ? { ...i, selected: checked } : i));
        }

        this.emitChanges();
    }

    private async addFiles(files: FileList): Promise<void> {
        const fileArray = Array.from(files);
        const inlineFiles: File[] = [];
        const oversizedFiles: File[] = [];

        for (const file of fileArray) {
            if (this.images.length + inlineFiles.length >= this.maxFiles) {
                this.errorService.openandCloseError(`Maximum number of images exceeded: ${this.maxFiles}`);
                return;
            }

            if (this.allowedFiles && !this.allowedFiles.test(file.name)) {
                this.errorService.openandCloseError(`File type not allowed: ${file.name}`);
                continue;
            }

            // Files that exceed inlineMaxFileSize should be offered for cloud upload
            if (this.inlineMaxFileSize && file.size > this.inlineMaxFileSize) {
                oversizedFiles.push(file);
            } else {
                inlineFiles.push(file);
            }
        }

        // Process inline files as before
        for (const file of inlineFiles) {
            const read = await this.readAsDataUrl(file).catch(() => null);
            if (!read) return;

            const id = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`).toString();
            const createdAt = new Date().toISOString();
            const selected = !this.images.some((i) => i.selected);

            this.images = (this.images || []).concat([
                {
                    id,
                    fileName: file.name,
                    contentType: file.type || 'image/*',
                    size: file.size,
                    createdAt,
                    dataUrl: read.dataUrl,
                    base64: read.base64,
                    selected
                }
            ]);

            this.emitChanges();
        }

        // If there are oversized files, ask user whether to upload to cloud
        if (oversizedFiles.length) {
            // Use shared ConfirmDialogComponent to ask user whether to upload oversized files to cloud
            const confirmRef = this.dialog.open(ConfirmDialogComponent, {
                data: {
                    title: 'Large files detected',
                    message: `You selected ${oversizedFiles.length} file(s) larger than ${this.formatBytes(this.inlineMaxFileSize)}.\n\nUpload to cloud storage (recommended) or embed inline (may be slow)?`,
                    confirmText: 'Upload to Cloud',
                    cancelText: 'Embed Inline'
                },
                width: '480px',
                autoFocus: false
            });

            const result = await confirmRef.afterClosed().toPromise();
            if (!result) {
                // User chose to embed inline — process oversized as inline (may be heavy)
                for (const file of oversizedFiles) {
                    const read = await this.readAsDataUrl(file).catch(() => null);
                    if (!read) return;

                    const id = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`).toString();
                    const createdAt = new Date().toISOString();
                    const selected = !this.images.some((i) => i.selected);

                    this.images = (this.images || []).concat([
                        {
                            id,
                            fileName: file.name,
                            contentType: file.type || 'image/*',
                            size: file.size,
                            createdAt,
                            dataUrl: read.dataUrl,
                            base64: read.base64,
                            selected
                        }
                    ]);

                    this.emitChanges();
                }
                return;
            }

            // Open UploadUiDialogComponent in a dialog to handle cloud upload
            try {
                const dRef: any = this.dialog.open(UploadUiDialogComponent as any, {
                    maxWidth: '900px',
                    width: '90vw',
                    data: { maxFiles: oversizedFiles.length },
                    autoFocus: false
                });

                // Subscribe to dialog close which returns uploaded files
                dRef.afterClosed().subscribe((result: any) => {
                    if (Array.isArray(result) && result.length) {
                        this.cloudFiles.emit(result);
                    }
                });
            } catch (e) {
                console.error('Failed to open Upload dialog', e);
                this.errorService.openandCloseError('Failed to open cloud upload dialog');
            }
        }
    }

    private async replaceWithFile(imageId: string, file: File): Promise<void> {
        if (this.allowedFiles && !this.allowedFiles.test(file.name)) {
            this.errorService.openandCloseError(`File type not allowed: ${file.name}`);
            return;
        }

        if (this.maxFileSize && file.size > this.maxFileSize) {
            this.errorService.openandCloseError(`File size exceeds limit (${this.maxFileSize} bytes): ${file.name}`);
            return;
        }

        const read = await this.readAsDataUrl(file).catch(() => null);
        if (!read) return;

        this.images = (this.images || []).map((img) =>
            img.id === imageId
                ? {
                    ...img,
                    fileName: file.name,
                    contentType: file.type || img.contentType,
                    size: file.size,
                    createdAt: new Date().toISOString(),
                    dataUrl: read.dataUrl,
                    base64: read.base64
                }
                : img
        );

        this.emitChanges();
    }

    private readAsDataUrl(file: File): Promise<{ dataUrl: string; base64: string }> {
        this.isReading = true;

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            this.currentReader = reader;

            reader.onerror = () => {
                this.isReading = false;
                this.currentReader = null;
                reject(new Error('read-error'));
            };

            reader.onabort = () => {
                this.isReading = false;
                this.currentReader = null;
                reject(new Error('read-aborted'));
            };

            reader.onload = () => {
                this.isReading = false;
                this.currentReader = null;

                const dataUrl = (reader.result || '').toString();
                const comma = dataUrl.indexOf(',');
                const base64 = comma >= 0 ? dataUrl.substring(comma + 1) : '';

                if (!base64) {
                    reject(new Error('empty-base64'));
                    return;
                }

                resolve({ dataUrl, base64 });
            };

            reader.readAsDataURL(file);
        });
    }

    private formatBytes(bytes: number): string {
        if (!bytes || bytes <= 0) return '0 B';
        const units = ['B', 'KB', 'MB', 'GB'];
        const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
        const value = bytes / Math.pow(1024, exponent);
        const precision = value >= 10 || exponent === 0 ? 0 : 1;
        return `${value.toFixed(precision)} ${units[exponent]}`;
    }

    private emitChanges(): void {
        const list = Array.isArray(this.images) ? [...this.images] : [];
        this.imagesChanged.emit(list);
        this.selectedImagesChanged.emit(list.filter((i) => i.selected));
    }

    openInNewTab(url: string | undefined | null, event?: Event): void {
        if (!url) return;
        event?.stopPropagation();
        try {
            window.open(url, '_blank');
        } catch (e) {
            console.error('Failed to open URL', e);
        }
    }
}
